import { Link } from 'react-router-dom';
import { ServicesSelect } from './services-select';
import { LanguageSelect } from './language-select';
import { ArrowCircleRight } from '@untitled-ui/icons-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useUserInfo } from '@/context/user-info';

interface Props {
  isOpen: boolean;
  servicesMenuOpen: boolean;
  isDrivingLessonsPage: boolean;
  onMenuOpen: () => void;
  onClickHowItWorks: () => void;
  onClickAboutApp: () => void;
  onClickDrivingLessons: () => void;
  onClickBusiness: () => void;
  onClickGiftCard: () => void;
  onClickDownload: () => void;
}

export const MobileNavbar = ({
  isOpen,
  servicesMenuOpen,
  isDrivingLessonsPage,
  onMenuOpen,
  onClickHowItWorks,
  onClickAboutApp,
  onClickDrivingLessons,
  onClickBusiness,
  onClickGiftCard,
  onClickDownload,
}: Props) => {
  const { userLanguage } = useUserInfo();
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'fixed top-0 left-0 pt-34 w-full h-screen md:hidden bg-bg-dark text-white origin-top transition-transform duration-300',
        {
          'scale-y-0': !isOpen,
          'scale-y-100': isOpen,
        },
      )}
    >
      <div
        className={cn({
          'size-full flex flex-col items-center justify-between transition-opacity duration-1000': true,
          'opacity-0': !isOpen,
          'opacity-100': isOpen,
        })}
      >
        <div className="size-full flex flex-col items-center gap-8">
          <ServicesSelect
            servicesMenuOpen={servicesMenuOpen}
            onMenuOpen={onMenuOpen}
            onClickHowItWorks={onClickHowItWorks}
            onClickAboutApp={onClickAboutApp}
          />
          <div className="size-full flex flex-col items-center gap-8 transition-all duration-200">
            {!isDrivingLessonsPage && (
              <Link
                to={`/${userLanguage}/driving-lessons`}
                className="text-xl font-semibold uppercase"
                onClick={onClickDrivingLessons}
                aria-label={t('translation:accessibility.button.go_driving_lessons')}
              >
                <p>{t('translation:navbar.link.driving_lessons')}</p>
              </Link>
            )}
            <Link
              to="https://www.rideyego.com/business"
              className="text-xl font-semibold uppercase"
              onClick={onClickBusiness}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('translation:accessibility.button.go_business')}
            >
              {t('translation:navbar.link.business')}
            </Link>
            <Link
              to="https://www.rideyego.com/gifts"
              className="text-xl font-semibold uppercase"
              onClick={onClickGiftCard}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('translation:accessibility.button.go_gifts')}
            >
              {t('translation:navbar.link.gift_card')}
            </Link>

            <LanguageSelect isMobile={isOpen} />
          </div>
        </div>
        <Link
          to={
            navigator.userAgent.match(/iPhone|iPad|iPod|Mac/i)
              ? 'https://itunes.apple.com/es/app/yugo-mobility/id1181020675?mt=8'
              : 'https://play.google.com/store/apps/details?id=com.getyugo.app'
          }
          target="_blank"
          rel="noopener noreferrer"
          className="h-24 w-full bg-bg-orange flex items-center justify-between px-8"
          onClick={onClickDownload}
          aria-label={t('translation:accessibility.button.download_app')}
        >
          <p className="text-2xl font-phosphate drop-shadow-sm uppercase">
            {t('translation:navbar.link.download')}
          </p>
          <ArrowCircleRight className="size-6 text-white drop-shadow-sm" />
        </Link>
      </div>
    </div>
  );
};
